import { type DocPageId, isDocPage } from "@/lib/docs-pages";

/**
 * A URL cut after each path slash, where a line may break:
 * "https://mcp.deceuster.be/connector/mcp" → "https://mcp.deceuster.be/", "connector/", "mcp".
 * The slashes of "https://" stay together.
 */
export function urlSegments(url: string): string[] {
  return url.split(/(?<=[^/:]\/)/);
}

/** A run of docs text: plain text, or a link to a page of the docs. */
export type TextRun = string | { page: DocPageId };

/**
 * Docs text cut at its links to other pages, which the content writes as
 * `[[<page>]]` ("(see [[connect-claude]])"). A link to a page that does not
 * exist stays as it is written, so it shows rather than vanishes; the content
 * tests catch it.
 */
export function pageLinks(text: string): TextRun[] {
  return text
    .split(/\[\[([^\]]*)\]\]/)
    .map((part, index): TextRun => {
      if (index % 2 === 0) {
        return part;
      }
      return isDocPage(part) ? { page: part } : `[[${part}]]`;
    })
    .filter((run) => run !== "");
}

/** A run of docs text, in bold or not. */
export interface TextPart {
  text: string;
  strong: boolean;
}

/**
 * Docs text cut into its plain and bold runs: the content marks bold with
 * `**…**`, as in Markdown ("Open **Customize → Connectors**.").
 */
export function emphasis(text: string): TextPart[] {
  return text
    .split("**")
    .map((part, index) => ({ text: part, strong: index % 2 === 1 }))
    .filter((part) => part.text !== "");
}

/**
 * French typography: a no-break space before `: ; ! ?` and `»` and after `«`,
 * so a line never starts with one. The French content is written with plain
 * spaces there, which keeps it readable; this turns them into no-break ones.
 */
export function frenchSpacing(text: string): string {
  return text.replace(/ (?=[:;!?»])/g, "\u00a0").replace(/« /g, "«\u00a0");
}

/** `value` with `transform` applied to every string in it, however deep. */
export function mapStrings<T>(value: T, transform: (text: string) => string): T {
  if (typeof value === "string") {
    return transform(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item: unknown) => mapStrings(item, transform)) as T;
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, mapStrings(item, transform)]),
    ) as T;
  }
  return value;
}

/**
 * A part of the page on connecting Claude, after its intro: the Owner's steps
 * under their heading (`owner`), everyone's steps (`everyone`), the note on
 * who can sign in (`access`), and the Owner's steps folded away under a title
 * to click (`ownerFolded`).
 */
export type ConnectPart = "owner" | "everyone" | "access" | "ownerFolded";

/**
 * The parts of the page on connecting Claude, in order. An admin, who sets
 * the connector up, reads the Owner's steps first, then everyone's. A
 * colleague reads their own steps first, ending with the test question, then
 * who can sign in, and finds the Owner's steps folded away at the end.
 */
export function connectParts(isAdmin: boolean): readonly ConnectPart[] {
  return isAdmin ? ["owner", "everyone", "access"] : ["everyone", "access", "ownerFolded"];
}

/**
 * Whether everyone's steps come after the Owner's, shown open: their heading
 * may then start with "then" ("Daarna, voor iedereen"); when they come first,
 * it may not.
 */
export function everyoneFollowsOwner(parts: readonly ConnectPart[]): boolean {
  const owner = parts.indexOf("owner");
  return owner !== -1 && owner < parts.indexOf("everyone");
}
